import React, { useEffect, useRef, useState } from "react"
import CMSRender from "aldoo-ra/CMS/cms-render"
import Typography from "aldoo-ra/Typography"
import parse from "html-react-parser"

const PWUCards = () => {
  const containerRef = useRef(null)
  const leftSectionRef = useRef(null)
  const rightSectionRef = useRef(null)
  const [leftStyle, setLeftStyle] = useState({})
  const [position, setPosition] = useState("initial") // 'initial', 'fixed', or 'absolute'

  useEffect(() => {
    const handleScroll = () => {
      if (
        !containerRef.current ||
        !leftSectionRef.current ||
        !rightSectionRef.current
      )
        return

      const containerRect = containerRef.current.getBoundingClientRect()
      const leftRect = leftSectionRef.current.getBoundingClientRect()
      const rightRect = rightSectionRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const leftHeight = leftRect.height

      // Calculate the point where left section should become fixed
      const centerPoint = windowHeight / 2 - leftHeight / 2

      if (containerRect.top > centerPoint) {
        // Initial scroll - both sections scroll normally
        setPosition("initial")
        setLeftStyle({
          position: "absolute",
          top: "0px",
        })
      } else if (rightRect.bottom > windowHeight / 2 + leftHeight / 2) {
        // Middle scroll - left section is fixed in center
        setPosition("fixed")

        setLeftStyle({
          position: "fixed",
          top: centerPoint + "px",
          width: leftRect.width + "px",
        })
      } else {
        // End scroll - left section becomes absolute
        // Calculate the bottom offset based on the container's remaining height
        const containerBottom = containerRect.bottom
        const rightBottom = rightRect.bottom
        const bottomOffset = containerBottom - rightBottom

        setPosition("absolute")
        setLeftStyle({
          position: "absolute",
          top: `${
            rightRect.height - leftHeight - bottomOffset + centerPoint
          }px`,
          width: leftRect.width + "px",
        })
      }
    }

    window.addEventListener("scroll", handleScroll)
    // Initial calculation
    handleScroll()

    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <CMSRender
      contentType="PWUCardSection"
      renderItem={(data, id) => {
        return (
          <div
            key={id}
            ref={containerRef}
            className="relative w-full bg-gradient_why_pwu px-[16px] md:px-px1_l xl:px-px1_xl pt-[70px] md:pt-[117px] pb-[80px]"
          >
            <div 
              className="w-full lg:max-w-w2_l xl:max-w-w2_xl flex flex-col lg:flex-row items-center justify-between gap-[30px] mx-auto"
            >
              {/* Left Side, Vertically Centered */}
              <div 
                ref={leftSectionRef}
                style={leftStyle}
                className="relative hidden lg:flex lg:max-w-[420px] flex-shrink-0 items-center lg:pl-[10px] lg:pt-[250px] pb-[70px] md:pb-[117px] lg:pb-[150px]"
              >
                <Typography
                  component="h2"
                  variant="h1"
                  className="text-text text-center lg:text-left"
                  style={{ zIndex: 1 }}
                >
                  {data.title && typeof data.title === 'string' ? parse(data.title) : data.title}
                </Typography>

                {/* Blurred circle background on the left */}
                <div
                  className="absolute left-0 top-0 lg:top-[230px] h-[122px] w-[122px] rounded-full bg-orange_primary/50"
                  style={{
                    filter: "blur(50px)",
                    // transform: "translate(50%, 20%)",
                  }}
                />
              </div>

              {/* Cards Stack - Right Side */}
              <div
                ref={rightSectionRef} 
                className="flex flex-col w-full lg:w-[67%] max-w-[704px] lg:max-w-[600px] xl:max-w-[760px] gap-[20px] items-center lg:items-end lg:ml-auto"
              >
                {data.cards?.map((card, index) => (
                  <div
                    key={index}
                    className="flex flex-col w-full bg-paper rounded-[50px] px-[44px] py-[30px] border border-secondary gap-[18px]"
                  >
                    {/* Number */}
                    <Typography
                      variant="h3"
                      className="font-medium bg-clip-text text-transparent bg-gradient-to-t from-grad_blue_dk to-grad_blue_lt"
                    >
                      {String(index + 1).padStart(2, "0")}
                    </Typography>

                    {/* Card Title */}
                    <Typography
                      variant="h3"
                      className="font-bold"
                    >
                      {card.title}
                    </Typography>

                    {/* Card Description */}
                    <Typography variant="body1" className="text-gray-600">
                      {card.description}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      }}
    />
  )
}

export default PWUCards
